/**
 * Typed error vocabulary. `ApiError` itself comes from the vendored factory —
 * re-exported here so call sites have a single import and never reach into the
 * vendored module directly.
 *
 * Codes native to the saas-auth `{success:false,error:{code,message}}` envelope
 * belong here. The tmm backend's envelope is not yet defined (P6 held); when the first
 * operational page lands it gets its own local mapping.
 *
 * TMMPDM-0063 (TMM-B3): the server's error text is never shown. `errorCopy` maps a code to catalog
 * copy; this file is the one place (with the vendored factory) that may touch an `ApiError`'s
 * `details.message`, and nothing here returns it. `src/errorText.guard.test.ts` enforces it.
 *
 * `CODE_INVALID` is `POST /api/auth/session/exchange`'s single generic
 * rejection (unknown, malformed, expired, replayed, unbound — no oracle).
 */
import { ApiError } from './httpClientFactory';

export { ApiError };

export type ErrorCode =
  // Auth round-trip / session
  | 'INVALID_RETURN_URL'
  | 'SESSION_EXPIRED'
  | 'CSRF_VALIDATION_FAILED'
  | 'INVALID_CREDENTIALS'
  | 'CODE_INVALID'
  // Product-admin access
  | 'PRODUCT_ADMIN_REQUIRED'
  | 'PRODUCT_ADMIN_REVOKED'
  | 'PRODUCT_CONTEXT_MISMATCH'
  // Cross-cutting
  | 'TENANT_ORIGIN_MISMATCH'
  | 'RATE_LIMIT_REJECTED'
  | 'INVALID_LOCALE'
  // UI-synthesized
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

const KNOWN_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'INVALID_RETURN_URL',
  'SESSION_EXPIRED',
  'CSRF_VALIDATION_FAILED',
  'INVALID_CREDENTIALS',
  'CODE_INVALID',
  'PRODUCT_ADMIN_REQUIRED',
  'PRODUCT_ADMIN_REVOKED',
  'PRODUCT_CONTEXT_MISMATCH',
  'TENANT_ORIGIN_MISMATCH',
  'RATE_LIMIT_REJECTED',
  'INVALID_LOCALE',
  'NETWORK_ERROR',
  'UNKNOWN',
]);

export function mapBackendCode(code: string | undefined): ErrorCode {
  if (!code) return 'UNKNOWN';
  if (KNOWN_CODES.has(code)) return code as ErrorCode;
  if (code === 'INVALID_CSRF') return 'CSRF_VALIDATION_FAILED';
  if (code === 'INVALID_ORIGIN') return 'TENANT_ORIGIN_MISMATCH';
  return 'UNKNOWN';
}

/** The two 401 codes that mean the grant is gone or mismatched. A caller seeing
 *  either must re-login — it must never retry into a loop. */
export function isGrantRevocation(code: string | undefined): boolean {
  return code === 'PRODUCT_ADMIN_REVOKED' || code === 'PRODUCT_CONTEXT_MISMATCH';
}

/**
 * tmm-api 502 `SAAS_AUTH_OUTCOME_UNKNOWN` (TMMAPI-0074 handoff §5): a pass-through write timed
 * out or saas-auth 5xx'd and MAY have happened. The caller re-reads the resource — it never
 * re-POSTs — and shows `common.outcomeUnknown`.
 */
export function isOutcomeUnknown(error: unknown): error is ApiError {
  return error instanceof ApiError && String(error.code) === 'SAAS_AUTH_OUTCOME_UNKNOWN';
}

/**
 * tmm-api 502 `UPSTREAM_UNAVAILABLE` (TMMAPI-0096) on tenant create / PATCH / reseller
 * assignment: saas-auth's reply was incomplete and NOTHING was written, so re-sending the
 * identical body is safe. Distinct from `SAAS_AUTH_OUTCOME_UNKNOWN`, which re-reads and never
 * re-sends.
 */
export function isUpstreamUnavailable(error: unknown): error is ApiError {
  return error instanceof ApiError && String(error.code) === 'UPSTREAM_UNAVAILABLE';
}

/** The one message every tenant write shows for `UPSTREAM_UNAVAILABLE`, beside its Try again. */
export function upstreamUnavailableMessage(t: (key: string, fallback?: string) => string): string {
  return t(
    'tenants.upstreamUnavailable',
    "The sign-in service didn't respond completely. Nothing was changed — try again.",
  );
}

/**
 * tmm-api 403 `ACTING_USER_NOT_PRODUCT_ADMIN` (handoff §5): the admin's grant was revoked as
 * saas-auth saw it. The panel shows its catalog copy (`errors.code.*`); nothing retries it.
 */
export function isActingAdminRevoked(error: unknown): error is ApiError {
  return error instanceof ApiError && String(error.code) === 'ACTING_USER_NOT_PRODUCT_ADMIN';
}

/** `VALIDATION_FAILED` by the normalised code — never by sniffing an unclassified body. */
export function isValidationFailed(error: unknown): error is ApiError {
  return error instanceof ApiError && String(error.code) === 'VALIDATION_FAILED';
}

/**
 * The fields a `VALIDATION_FAILED` marks, from the backend's `{ fields: { <field>: <message> } }` in
 * `details.backend` (fleet 2026.09.20), each with CATALOG text that says how to fix it (TMM-B3). The
 * server's per-field message is never shown: `rules` carries the form's own client copy for a field
 * whose rule it knows (range, required, length, format); every other field gets the generic
 * `errors.field.rejected`. Untyped on the wire — narrowed here, once.
 */
export function validationFields(
  error: ApiError,
  t: ErrorTranslate,
  rules: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const fields = error.details.backend?.['fields'];
  if (!fields || typeof fields !== 'object') return {};
  return catalogFields(fields as Record<string, unknown>, t, rules);
}

/**
 * A server `{ <field>: <message> }` map (a 400's `details.fields`, or a dry-run preview's `fields`)
 * with the server's text replaced by catalog text: the field names say WHICH fields failed, the
 * copy says what to do. `rules` is the form's own client copy for a field whose rule it knows.
 */
export function catalogFields(
  fields: Readonly<Record<string, unknown>>,
  t: ErrorTranslate,
  rules: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, message] of Object.entries(fields)) {
    if (typeof message !== 'string') continue;
    out[field] =
      rules[field] ??
      t('errors.field.rejected', "This value isn't accepted. Check it and try again.");
  }
  return out;
}

/**
 * tmm-api 409 `COMMISSION_PLAN_NAME_TAKEN` (TMMAPI-0109) on a plan create, copy or PATCH: the name
 * another of the reseller's plans has, from `details.name` (`''` when the server sent none);
 * `null` for any other error.
 */
export function commissionPlanNameTaken(error: unknown): string | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'COMMISSION_PLAN_NAME_TAKEN') {
    return null;
  }
  const name = error.details.backend?.['name'];
  return typeof name === 'string' ? name : '';
}

/**
 * tmm-api 409 `PARKED_EVENT_STILL_REFUSED` (TMMAPI-0121) on a re-apply: the event was refused again and
 * nothing changed. Returns the refusal's `details.reasonCode` (`''` when the server sent none); `null`
 * for any other error. `details.exceptionClass` is never read: it is for support, not for display.
 */
export function parkedEventRefusal(error: unknown): string | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'PARKED_EVENT_STILL_REFUSED') {
    return null;
  }
  const reasonCode = error.details.backend?.['reasonCode'];
  return typeof reasonCode === 'string' ? reasonCode : '';
}

/**
 * tmm-api 409 `RESELLER_SUSPENDED` (0094) / `RESELLER_DEACTIVATED` (0084): that reseller can't
 * receive tenants. One catalog message on every write that can meet them (TMMPDM-0029) — callers
 * try this ahead of their own fallback; `null` for any other error.
 */
export function resellerRefusalMessage(
  error: unknown,
  t: (key: string, fallback?: string) => string,
): string | null {
  const code = error instanceof ApiError ? String(error.code) : null;
  if (code === 'RESELLER_SUSPENDED') {
    return t('tenants.reseller.suspended', 'That reseller is suspended and can’t receive tenants.');
  }
  if (code === 'RESELLER_DEACTIVATED') {
    return t(
      'tenants.reseller.deactivated',
      'That reseller is deactivated and can’t take tenants. Choose another.',
    );
  }
  return null;
}

/**
 * A write against a stale `version` (tmm-api unit 0112: 409 `<ENTITY>_VERSION_CONFLICT`) or one that
 * lost a lock (409 `CONCURRENT_UPDATE`, retryable): the editor offers Reload. Any other 409 is
 * its own code (`RATE_CARD_NAME_TAKEN`, `RATE_CARD_IMMUTABLE`, …) with its own copy; a status alone
 * never means stale.
 */
export function isStaleWrite(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const code = String(error.code);
  return code.endsWith('_VERSION_CONFLICT') || code === 'CONCURRENT_UPDATE';
}

/**
 * tmm-api 410 `PAYOUTS_MOVED_TO_LEDGER` (TMMAPI-0162 item 31): from `LIVE`, an old payout action
 * (record a payout, release an old hold, retry a reseller charge) is retired. The dialog says so
 * with a link to the partner's Ledger, and the screen re-reads.
 */
export function isPayoutsMovedToLedger(error: unknown): error is ApiError {
  return error instanceof ApiError && String(error.code) === 'PAYOUTS_MOVED_TO_LEDGER';
}

/**
 * tmm-api 409 `TICKET_TRANSITION_INVALID {current}` (TMMAPI-0181 item 30, confirmed at `56a8fa38`):
 * the type doesn't allow that status from the one the ticket has now. Returns `details.current` (the
 * status code; `''` when the server sent none); `null` for any other error.
 */
export function ticketTransitionCurrent(error: unknown): string | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'TICKET_TRANSITION_INVALID') {
    return null;
  }
  const current = error.details.backend?.['current'];
  return typeof current === 'string' ? current : '';
}

/**
 * tmm-api 422 `ANNOUNCEMENT_TERM_REFUSED {term}` (TMMAPI-0181 item 32a): the help library's leak
 * guard found an internal term on publish. Returns `details.term` (`''` when the server sent none);
 * `null` for any other error. The term is the author's own text echoed back, not server prose.
 */
export function announcementRefusedTerm(error: unknown): string | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'ANNOUNCEMENT_TERM_REFUSED') {
    return null;
  }
  const term = error.details.backend?.['term'];
  return typeof term === 'string' ? term : '';
}

/**
 * tmm-api 409 `ANNOUNCEMENT_NOT_PUBLISHED {state}` (TMMAPI-0181 U5, YAML at `3a2d9f5f`): Expire on an
 * announcement that isn't published. Returns `details.state` (the state code; `''` when the server
 * sent none); `null` for any other error.
 */
export function announcementNotPublishedState(error: unknown): string | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'ANNOUNCEMENT_NOT_PUBLISHED') {
    return null;
  }
  const state = error.details.backend?.['state'];
  return typeof state === 'string' ? state : '';
}

/**
 * tmm-api 409 `MERCHANT_PATTERN_NOT_PROMOTABLE {failedGates}` (TMMAPI-0194, YAML at `f7c843fb`): an
 * approval whose recomputed gates don't all pass; nothing changed. Returns `details.failedGates` (the
 * gate codes that are strings; `[]` when the server sent none); `null` for any other error.
 */
export function merchantPatternFailedGates(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'MERCHANT_PATTERN_NOT_PROMOTABLE') {
    return null;
  }
  const gates = error.details.backend?.['failedGates'];
  return Array.isArray(gates)
    ? gates.filter((gate): gate is string => typeof gate === 'string')
    : [];
}

/**
 * tmm-api 409 `MERCHANT_PATTERN_STATE {state}` (TMMAPI-0194, YAML at `f7c843fb`): approve or reject on
 * a pattern that isn't a candidate, or retire on one that isn't approved. Returns `details.state` (the
 * state code; `''` when the server sent none); `null` for any other error.
 */
export function merchantPatternState(error: unknown): string | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'MERCHANT_PATTERN_STATE') return null;
  const state = error.details.backend?.['state'];
  return typeof state === 'string' ? state : '';
}

/**
 * `INSTITUTION_EXISTS`'s `details` (TMMAPI-0196 item 37n). The id is never shown or linked. A parse
 * result, not the wire DTO: `null` means the key was absent or not a number, not a nullable field.
 */
export interface InstitutionExistsDetails {
  institutionId: number | null;
}

/**
 * tmm-api 409 `INSTITUTION_EXISTS {institutionId}` (TMMAPI-0196 item 37n, spec at `10661502`): an add
 * whose folded name or alias matches an active institution in that country; nothing changed, and the
 * admin dismisses the request instead. Returns the details (`institutionId` `null` when the server
 * sent none); `null` for any other error.
 */
export function institutionExists(error: unknown): InstitutionExistsDetails | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'INSTITUTION_EXISTS') return null;
  const institutionId = error.details.backend?.['institutionId'];
  return { institutionId: typeof institutionId === 'number' ? institutionId : null };
}

/** The catalog's `t` (`useCopy().t` fits): a key, and the English text to render if the key is absent. */
export type ErrorTranslate = (key: string, fallback?: string) => string;

/**
 * Every error code the console handles anywhere, each with generic catalog copy at
 * `errors.code.<CODE>` in all four catalogs (TMM-B3; `catalogCoverage.test.ts` asserts every value
 * is a string in `en.json`). A code a feature words more specifically (`tenants.reseller.suspended`,
 * `resellerBilling.errors.notRetryable`, …) is matched by that feature ahead of `errorCopy`; this
 * list is what everything else falls to.
 */
export const ERROR_CODES = [
  // Auth round-trip / session / access
  'SESSION_EXPIRED',
  'CSRF_VALIDATION_FAILED',
  'INVALID_CREDENTIALS',
  'CODE_INVALID',
  'INVALID_RETURN_URL',
  'PRODUCT_ADMIN_REQUIRED',
  'PRODUCT_ADMIN_REVOKED',
  'PRODUCT_CONTEXT_MISMATCH',
  'ACTING_USER_NOT_PRODUCT_ADMIN',
  'TENANT_ORIGIN_MISMATCH',
  'RATE_LIMIT_REJECTED',
  // saas-auth's own 429 code, worded the same (its console calls reach it for sign-in and members).
  'RATE_LIMITED',
  'INVALID_LOCALE',
  // Transport and write outcomes
  'NETWORK_ERROR',
  'VALIDATION_FAILED',
  'SAAS_AUTH_OUTCOME_UNKNOWN',
  'UPSTREAM_UNAVAILABLE',
  'CONCURRENT_UPDATE',
  // Tenants
  'TENANT_NOT_FOUND',
  'TENANT_SLUG_TAKEN',
  'TENANT_SETUP_INCOMPLETE',
  'TENANT_CURRENCY_LOCKED',
  'TENANT_NOT_PRICED',
  'TENANT_CURRENCY_NOT_PRICED',
  'PRODUCT_HAS_NO_DEFAULT_BRAND',
  'BRAND_SLUG_GENERATION_FAILED',
  'HOSTNAME_ALREADY_REGISTERED',
  'WHITELABEL_DOMAIN_INVALID',
  'MERCHANT_OF_RECORD_UNSET',
  // Resellers
  'RESELLER_SUSPENDED',
  'RESELLER_DEACTIVATED',
  'RESELLER_SUSPENDED_USE_REACTIVATE',
  'RESELLER_SUSPENDED_USE_STATUS',
  'RESELLER_NOT_DEACTIVATED',
  'RESELLER_STANDING_CONFLICT',
  'RESELLER_HAS_TENANT',
  'RESELLER_NOT_FOUND',
  'RESELLER_PLATFORM_OWNED',
  'RESELLER_HAS_TENANTS',
  'RESELLER_LAST_OWNER',
  'AGREEMENT_REQUIRED',
  'RESELLER_SLUG_TAKEN',
  'RESELLER_REQUIRED',
  'RESELLER_CONSOLE_NOT_CONFIGURED',
  'RESELLER_CHARGE_NOT_RETRYABLE',
  'RESELLER_CHARGE_NOT_FOUND',
  // Rate cards, commission plans, payouts, affiliates, deletions
  'RATE_CARD_NOT_ACTIVE',
  'OFFER_NOT_ACTIVE',
  'ADDON_ACTIVE',
  'RATE_CARD_IMMUTABLE',
  'RATE_CARD_NAME_TAKEN',
  'RATE_CARD_VERSION_CONFLICT',
  // Rate cards (TMMAPI-0131 / 0154). Feature screens word some of these more specifically.
  'RATE_CARD_INVALID',
  'NO_RATE_CARD',
  'CARD_ADOPTED',
  'RATE_CARD_NOT_FOUND',
  'COMMISSION_PLAN_IMMUTABLE',
  'COMMISSION_PLAN_NAME_TAKEN',
  'COMMISSION_PLAN_VERSION_CONFLICT',
  'PAYOUT_ON_HOLD',
  'PAYOUT_NOT_PAYABLE',
  'AFFILIATE_STATE',
  'DELETION_ALREADY_REQUESTED',
  // Erase now (TMMAPI-0157). The Deletion requests confirm words both more specifically.
  'DELETION_CANCELLED',
  'DELETION_REQUEST_NOT_FOUND',
  // Subscription promotions (TMMAPI-0124)
  'PROMOTION_NOT_APPLIED',
  'PROMOTION_ALREADY_APPLIED',
  'PROMOTION_NOT_REDEEMABLE',
  // A plan bound at pay-api that is not the one asked for (TMMAPI-0125): global, any plan-minting call
  'PLAN_MAPPING_CONFLICT',
  // pay-api refused the plan, or created it and refused the read that checks it (TMMAPI-0127, 502)
  'PLAN_CREATE_FAILED',
  // pay-api can't be reached (503; `details.reason` is never shown). A screen that words it
  // specifically (a Re-apply, the Payments setup panel) matches it ahead of `errorCopy`.
  'PAY_API_UNAVAILABLE',
  // The tenant holds a connected, other-provider or other-environment billing connection at pay-api
  // (409, TMMAPI-0130; `details.reason` is never shown). The Payments setup confirm words it by reason.
  'BILLING_CONNECTION_CONFLICT',
  // pay-api (Stripe Tax) has no usable billing location for the card (422, TMMAPI-0156): global.
  'TAX_LOCATION_REQUIRED',
  // Parked payment events (TMMAPI-0121)
  'PARKED_EVENT_STILL_REFUSED',
  'PARKED_EVENT_UNAVAILABLE',
  'PARKED_EVENT_RESOLVED',
  'PARKED_EVENT_NOT_FOUND',
  'PARKED_EVENT_BUSY',
  // Members and invitations
  'INVITATION_ALREADY_ACCEPTED',
  'INVITATION_ALREADY_PENDING',
  'ALREADY_ACTIVE_MEMBER',
  'LAST_OWNER_PROTECTED',
  'ROLE_CHANGE_CROSSES_TABLES',
  // GoHighLevel
  'GHL_LOCATION_NOT_VERIFIED',
  'GHL_LOCATION_ALREADY_CLAIMED',
  // The partner ledger (TMMPDM-0095; TMMAPI-0162 U4 relays pay-api's codes). The Ledger dialogs word
  // HOLD_OPS_ONLY and the manual payment's bound more specifically.
  'PAYOUTS_MOVED_TO_LEDGER',
  'HOLD_ALREADY_OPEN',
  'HOLD_NOT_OPEN',
  'HOLD_NOT_RELEASABLE',
  'HOLD_OPS_ONLY',
  'HOLD_NOT_VOIDABLE',
  'HOLD_NOT_FOUND',
  'NOTHING_TO_VOID',
  'SETTLEMENT_IN_FLIGHT',
  'CONFIRMATION_REQUIRED',
  'PAYEE_NOT_FOUND',
  'PAYEE_RAIL_MISMATCH',
  'PAYEE_ON_HOLD',
  'STATEMENT_NOT_PAYABLE',
  'STATEMENT_NOT_FOUND',
  'TAX_YEAR_CLOSED',
  'IDEMPOTENCY_CONFLICT',
  'LEDGER_CUTOVER_NOT_READY',
  // Tickets (TMMPDM-0109; TMMAPI-0181 U5): a deployed route's missing ticket. A bare 404 is "not
  // available yet", worded by the Tickets screens.
  'TICKET_NOT_FOUND',
  // TMMPDM-0110 (tmm-api 0181 item 30 at `56a8fa38`): the status dialog words the transition with
  // `details.current`; the duplicate refusal is worded as the contract gives it.
  'TICKET_TRANSITION_INVALID',
  'TICKET_DUPLICATE_INVALID',
  // TMMPDM-0110 (0181 item 32a): announcements. The editor words a refused term with the term itself.
  'ANNOUNCEMENT_NOT_FOUND',
  'ANNOUNCEMENT_TERM_REFUSED',
  // TMMPDM-0111 (0181 U5 YAML): an edit, publish or delete once it's no longer a draft, and Expire on
  // one that isn't published (worded with `details.state` by the editor). Both re-read it.
  'ANNOUNCEMENT_NOT_DRAFT',
  'ANNOUNCEMENT_NOT_PUBLISHED',
  // TMMPDM-0112 (tmm-api 0194 item 7, 0195 item 3, specs at `9ae13265`): a deployed route's missing
  // pattern or request, by the house `<ENTITY>_NOT_FOUND` form (the specs name no code). A bare 404 is
  // "not available yet", worded by the Merchant knowledge screens.
  'MERCHANT_PATTERN_NOT_FOUND',
  'INSTITUTION_REQUEST_NOT_FOUND',
  // TMMPDM-0113 (YAML at `f7c843fb`): the pattern dialogs word NOT_PROMOTABLE with `details.failedGates`
  // and STATE with `details.state`; these generic lines are for a refusal that sent neither.
  'MERCHANT_PATTERN_NOT_PROMOTABLE',
  'MERCHANT_PATTERN_STATE',
  'INSTITUTION_REQUEST_STATE',
  // TMMPDM-0114 (tmm-api 0196 item 37n, spec at `10661502`): the add dialog words it with the
  // dialog's country and offers Dismiss request; this generic line is for anywhere else.
  'INSTITUTION_EXISTS',
] as const;

const ERROR_CODE_SET: ReadonlySet<string> = new Set(ERROR_CODES);

/**
 * The catalog text for a failure (TMM-B3: the server's own text is never shown, and neither is a raw
 * code). A code on `ERROR_CODES` gets its `errors.code.<CODE>` copy; a bare 429 (no recognised code)
 * gets the rate-limit copy; anything else gets `fallback`, the calling screen's own catalog line for
 * what it was doing ("Couldn't save the changes.").
 */
export function errorCopy(error: unknown, t: ErrorTranslate, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const code = String(error.code);
  if (ERROR_CODE_SET.has(code)) return t(`errors.code.${code}`, fallback);
  if (error.status === 429) return t('errors.code.RATE_LIMIT_REJECTED', fallback);
  return fallback;
}
